import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { Sparkles, ImagePlus, ArrowRight, Check, Loader2 } from "lucide-react";
import { useAuth } from "@/features/auth/context/AuthContext";
import { readImageAsDataUrl } from "@/features/shared/imageUpload";
import { updateWorkspace, updateProfile } from "@/features/settings/settingsApi";
import { Logo } from "@/shared/brand";
import { EASE } from "@/shared/motion/transitions";

const COLORS = ["#C9A24A", "#0F172A", "#0E7490", "#9333EA", "#DC2626", "#059669"];

/**
 * First-run welcome wizard. Brand-new users (onboarded:false) land here: a short
 * animated flow that brands their auto-created workspace, then marks onboarding
 * complete and drops them into the Vault.
 */
export default function WelcomeWizard() {
  const { user, activeWorkspace, refreshWorkspaces, updateUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [name, setName] = useState(activeWorkspace?.workspaceName ?? "");
  const [logo, setLogo] = useState<string | undefined>(activeWorkspace?.workspaceLogo ?? undefined);
  const [primaryColor, setPrimaryColor] = useState(COLORS[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const pickLogo = async (file: File | undefined) => {
    if (!file) return;
    try { setLogo(await readImageAsDataUrl(file, 256)); } catch { setError("Could not read that image"); }
  };

  const finish = async () => {
    setSaving(true);
    setError(null);
    try {
      if (activeWorkspace?.workspaceId && name.trim()) {
        await updateWorkspace(activeWorkspace.workspaceId, { name: name.trim(), logo, primaryColor });
      }
      await updateProfile({ onboarded: true });
      updateUser({ onboarded: true });
      await refreshWorkspaces();
      navigate("/vault");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
      setSaving(false);
    }
  };

  const skip = async () => {
    setSaving(true);
    try {
      await updateProfile({ onboarded: true });
      updateUser({ onboarded: true });
      navigate("/vault");
    } catch {
      navigate("/vault");
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-canvas text-ink-900">
      <header className="nav-marble flex h-14 items-center justify-between border-b border-ink-100 px-5 sm:px-8">
        <Logo size="sm" showTagline tagline="Vault" />
        <button onClick={skip} className="text-[13px] font-medium text-ink-400 hover:text-ink-700">Skip for now</button>
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-[560px]">
          {/* Progress dots */}
          <div className="mb-6 flex justify-center gap-2">
            {[0, 1].map((i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-7 bg-gold-500" : "w-1.5 bg-ink-200"}`} />
            ))}
          </div>

          <AnimatePresence mode="wait">
            {step === 0 ? (
              <motion.div
                key="welcome"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="rounded-[28px] border border-ink-100/70 bg-white p-8 text-center shadow-card sm:p-12"
              >
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-gold-100 to-gold-50 text-gold-600">
                  <Sparkles size={26} />
                </div>
                <h1 className="font-headline text-[30px] leading-tight">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</h1>
                <p className="mx-auto mt-3 max-w-[400px] text-[14px] leading-relaxed text-ink-500">
                  Jewelitt Vault is your home for hosting, configuring, and sharing photoreal 3D jewelry.
                  Let's set up your studio — it takes 20 seconds.
                </p>
                <button
                  onClick={() => setStep(1)}
                  className="btn-press btn-shine mx-auto mt-7 flex items-center gap-2 rounded-xl bg-ink-900 px-6 py-3 text-[14px] font-semibold text-white hover:bg-ink-800"
                >
                  Get started <ArrowRight size={16} />
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="setup"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="rounded-[28px] border border-ink-100/70 bg-white p-8 shadow-card sm:p-10"
              >
                <h2 className="font-headline text-[24px] leading-tight">Set up your studio</h2>
                <p className="mt-1.5 text-[13.5px] text-ink-500">Give your workspace a name, logo, and color. You can change these anytime.</p>

                {error && <p className="mt-4 rounded-lg bg-rose/10 px-3 py-2 text-[12.5px] text-rose">{error}</p>}

                <div className="mt-6 flex items-start gap-4">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="relative flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-ink-200 bg-white text-ink-400 hover:border-gold-300"
                    title="Upload logo"
                  >
                    {logo ? <img src={logo} alt="" className="h-full w-full object-cover" /> : <ImagePlus size={20} />}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickLogo(e.target.files?.[0])} />
                  <div className="flex-1">
                    <label className="mb-1.5 block text-[12px] font-semibold uppercase tracking-wide text-ink-500">Studio name</label>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Vela Atelier"
                      autoFocus
                      className="w-full rounded-xl border border-ink-200 bg-white px-4 py-2.5 text-[14px] focus:border-gold-400 focus:outline-none focus:ring-2 focus:ring-gold-100"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label className="mb-1.5 block text-[12px] font-semibold uppercase tracking-wide text-ink-500">Brand color</label>
                  <div className="flex items-center gap-2">
                    {COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setPrimaryColor(c)}
                        className={`h-7 w-7 rounded-full border-2 transition-transform ${primaryColor === c ? "scale-110 border-ink-900" : "border-transparent"}`}
                        style={{ background: c }}
                        aria-label={c}
                      />
                    ))}
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between">
                  <button onClick={() => setStep(0)} className="text-[13px] font-medium text-ink-400 hover:text-ink-700">Back</button>
                  <button
                    onClick={finish}
                    disabled={saving || !name.trim()}
                    className="btn-press btn-shine flex items-center gap-2 rounded-xl bg-ink-900 px-6 py-3 text-[14px] font-semibold text-white hover:bg-ink-800 disabled:opacity-50"
                  >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Enter the Vault
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
